import { Button, Modal, Text } from "@marketplace/ui";
import { useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useTranslation } from "react-i18next";

import { setPromoModalAtom } from "../../atoms/product-list-page-atom";
import { usePutSetPromoMutation } from "../../hooks/mutations/use-put-set-promo-mutation";

type SetPromoModalProps = {
  opened: boolean;
  onClose: () => void;
};

export const SetPromoModal = ({ opened, onClose }: SetPromoModalProps) => {
  const { t: tProduct } = useTranslation("product");
  const { t: tCommon } = useTranslation("common");
  const { productId, isPromoAvailable } = useAtomValue(setPromoModalAtom);
  const resetModal = useResetAtom(setPromoModalAtom);

  const { mutateAsync, isPending } = usePutSetPromoMutation();
  const targetAction = isPromoAvailable ? "deactivate" : "activate";

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleConfirm = async () => {
    if (!productId) return;

    await mutateAsync({
      productId,
      is_promo_available: targetAction === "activate"
    });

    handleClose();
  };

  return (
    <Modal.Root
      opened={opened}
      onClose={handleClose}
      size={345}
      classNames={{
        header: "!p-6 !pb-0",
        body: "!p-6"
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Body>
          <Text
            type="heading"
            size="xxs"
            weight="semibold"
            className="text-center text-fjord-grey-500 mb-1"
          >
            {tProduct(`setPromoModal.${targetAction}.title`)}
          </Text>
          <Text
            type="paragraph"
            size="s"
            className="text-center text-fjord-grey-400 mb-6"
          >
            {tProduct(`setPromoModal.${targetAction}.description`)}
          </Text>
          <div className="flex gap-x-[14px]">
            <Button variant="secondary" fullWidth onClick={handleClose}>
              {tCommon("no")}
            </Button>
            <Button
              variant="primary"
              fullWidth
              loading={isPending}
              onClick={handleConfirm}
              className="text-[12px]"
            >
              {isPromoAvailable
                ? tProduct("setPromoModal.confirmInactive")
                : tProduct("setPromoModal.confirm")}
            </Button>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
